import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Compass } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { SearchBar } from '../../components/ui/SearchBar';
import { EmptyState } from '../../components/ui/EmptyState';
import { coachesApi } from '../../api/coaches';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';

export function CoachListScreen() {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const { pushScreen } = useTabNavigationStore();
  const { maxCoaches } = useSubscriptionLimits();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', bio: '' });

  const { data: coaches, isLoading } = useQuery({
    queryKey: ['coaches', currentAssistantId],
    queryFn: () => coachesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; phone?: string; email?: string; bio?: string }) =>
      coachesApi.create(currentAssistantId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaches', currentAssistantId] });
      setShowCreateForm(false);
      setFormData({ name: '', phone: '', email: '', bio: '' });
    },
    onError: (error) => {
      console.error('Failed to create coach:', error);
      alert('Не удалось создать тренера. Попробуйте еще раз.');
    },
  });

  const handleCreateCoach = () => {
    if (!formData.name.trim()) return;
    createMutation.mutate({
      name: formData.name,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      bio: formData.bio || undefined,
    });
  };

  const filteredCoaches = coaches?.filter((coach) =>
    coach.name.toLowerCase().includes(search.toLowerCase())
  );

  const currentCount = coaches?.length || 0;
  const canCreateMore = maxCoaches === -1 || currentCount < maxCoaches;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-hint)',
    marginBottom: '4px',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  // -- No assistant selected -------------------------------------------------

  if (!currentAssistantId) {
    return (
      <EmptyState
        icon={Compass}
        title="Направление не выбрано"
        description="Выберите или создайте направление, чтобы начать работу с тренерами"
        primaryAction={{
          label: 'Выбрать направление',
          onClick: () => {
            try {
              window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
            } catch {}
            // Open direction switcher
            const { openDirectionSwitcher } = useTabNavigationStore.getState();
            openDirectionSwitcher();
          },
        }}
      />
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader title="Тренеры" />

      <SearchBar placeholder="Поиск по имени..." value={search} onChange={setSearch} />

      {!showCreateForm && (
        <>
          <div className="space-y-2">
            {filteredCoaches?.map((coach) => (
              <Card
                key={coach.id}
                onClick={() => pushScreen('coach-detail', { coachId: coach.id })}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{coach.name}</h3>
                    {coach.phone && <p className="text-sm" style={{ color: 'var(--text-hint)' }}>{coach.phone}</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {(!filteredCoaches || filteredCoaches.length === 0) && !search && (
            <Card>
              <div className="text-center py-8">
                <p className="mb-4" style={{ color: 'var(--text-hint)' }}>У вас пока нет тренеров</p>
                {canCreateMore ? (
                  <Button onClick={() => setShowCreateForm(true)}>Добавить первого тренера</Button>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
                    <button onClick={() => pushScreen('subscription-manage')} style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                      Обновите тариф
                    </button>{' '}
                    для создания тренеров.
                  </p>
                )}
              </div>
            </Card>
          )}

          {filteredCoaches?.length === 0 && search && (
            <Card>
              <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>Тренеры не найдены</p>
            </Card>
          )}

          {filteredCoaches && filteredCoaches.length > 0 && (
            canCreateMore ? (
              <Button onClick={() => setShowCreateForm(true)} variant="secondary" fullWidth>
                + Добавить тренера
              </Button>
            ) : (
              <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
                  Создан {currentCount} из {maxCoaches} тренеров.{' '}
                  <button onClick={() => pushScreen('subscription-manage')} style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                    Обновите тариф
                  </button>
                </p>
              </div>
            )
          )}
        </>
      )}

      {showCreateForm && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Добавить тренера</h3>
          <div className="space-y-4">
            <div>
              <label style={labelStyle}>ФИО *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Например: Иванов Иван Иванович" style={inputStyle} disabled={createMutation.isPending} />
            </div>
            <div>
              <label style={labelStyle}>Телефон</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+7 (999) 123-45-67" style={inputStyle} disabled={createMutation.isPending} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="coach@example.com" style={inputStyle} disabled={createMutation.isPending} />
            </div>
            <div>
              <label style={labelStyle}>О тренере</label>
              <textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder="Информация о тренере..." rows={3} style={{ ...inputStyle, resize: 'none' }} disabled={createMutation.isPending} />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateCoach} disabled={createMutation.isPending || !formData.name.trim()} fullWidth>
                {createMutation.isPending ? 'Создание...' : 'Создать'}
              </Button>
              <Button onClick={() => { setShowCreateForm(false); setFormData({ name: '', phone: '', email: '', bio: '' }); }} disabled={createMutation.isPending} variant="secondary">
                Отмена
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
